import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { flush, pendingCount, subscribe } from '@/shared/lib/offlineQueue';
import { toast } from '@/shared/lib/toast';
import {
  createExpense,
  updateExpense,
  softDeleteExpense,
  expensesKeys,
} from '@/features/expenses/api';
import { groupsKeys } from '@/features/groups/api';
import type { ExpenseCreateInput } from '@shared/schemas/expenses';
import { useOnlineStatus } from './useOnlineStatus';

/**
 * Escucha cambios en la cola offline y publica el count al React tree.
 * Además dispara `flush` automáticamente cuando el user vuelve online.
 *
 * Mount único en el App (post-auth). El resto del código usa `enqueue()`
 * directo sin depender de este hook.
 */
export function useOfflineQueue(): { pending: number } {
  const { t } = useTranslation();
  const [pending, setPending] = useState(0);
  const online = useOnlineStatus();
  const qc = useQueryClient();

  useEffect(() => {
    let alive = true;
    const refresh = () => {
      void pendingCount().then((n) => {
        if (alive) setPending(n);
      });
    };
    refresh();
    const unsub = subscribe(refresh);
    return () => { alive = false; unsub(); };
  }, []);

  useEffect(() => {
    if (!online || pending === 0) return;
    void (async () => {
      const { ok, fail } = await flush({
        createExpense: (payload) => createExpense(payload as ExpenseCreateInput),
        // Sprint L: update/delete offline.
        updateExpense: (payload) => {
          const p = payload as { expenseId: string; input: ExpenseCreateInput };
          return updateExpense(p.expenseId, p.input);
        },
        deleteExpense: (payload) => {
          const p = payload as { expenseId: string };
          return softDeleteExpense(p.expenseId);
        },
      });
      if (ok > 0) {
        toast.success(t('offline.synced', { count: ok }));
        // Invalidar caches para que la UI refleje los cambios sincronizados.
        void qc.invalidateQueries({ queryKey: expensesKeys.all });
        void qc.invalidateQueries({ queryKey: groupsKeys.all });
      }
      if (fail > 0) {
        toast.error(t('offline.sync_failed', { count: fail }));
      }
    })();
  }, [online, pending, qc, t]);

  return { pending };
}
